// The samples are silent, but the unmuted audio track still exercises autoplay blocking.
const SILENT_AUDIO_URL =
  'data:audio/wav;base64,UklGRnQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YVAAAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgA==';

export class AutoplayPermission {
  constructor({ audioFactory = (source) => new Audio(source) } = {}) {
    this.audioFactory = audioFactory;
    this.blockedMedia = new Map();
    this.initialPromptPending = false;
    this.permissionGranted = false;
    this.permissionProbe = null;
    this.promptVisible = false;
  }

  check(guideUrl) {
    this.refreshPrompt(guideUrl);
    if (this.permissionGranted || this.permissionProbe) return;

    if (this.#autoplayPolicy() === 'allowed') {
      this.permissionGranted = true;
      return;
    }

    return this.#probePlayback(guideUrl);
  }

  play(media, { guideUrl, onError }) {
    this.refreshPrompt(guideUrl);
    this.blockedMedia.delete(media);
    return this.#attemptPlayback(media, { guideUrl, onError });
  }

  forget(media) {
    this.blockedMedia.delete(media);
    if (this.blockedMedia.size === 0 && !this.initialPromptPending) this.#clearPrompt();
  }

  refreshPrompt(guideUrl) {
    const currentModalFrame = document.getElementById('modal');
    if (this.promptVisible && (!this.modalFrame?.isConnected || this.modalFrame !== currentModalFrame)) {
      this.#clearPrompt();
    }

    if (this.blockedMedia.size > 0) this.#showPrompt(guideUrl);
  }

  #attemptPlayback(media, options) {
    let playPromise;

    try {
      playPromise = media.play();
    } catch (error) {
      this.#handlePlaybackError(media, error, options);
      return;
    }

    if (!playPromise) {
      options.onSuccess?.();
      return;
    }

    return playPromise
      .then(() => options.onSuccess?.())
      .catch((error) => this.#handlePlaybackError(media, error, options));
  }

  #handlePlaybackError(media, error, options) {
    if (error.name !== 'NotAllowedError') {
      options.onError?.(error);
      return;
    }

    this.blockedMedia.set(media, options);
    this.#showPrompt(options.guideUrl);
  }

  #probePlayback(guideUrl) {
    const probe = this.audioFactory(SILENT_AUDIO_URL);
    probe.muted = false;
    probe.preload = 'auto';
    probe.volume = 1;
    this.initialPromptPending = true;
    this.permissionProbe = probe;

    return this.#attemptPlayback(probe, {
      guideUrl,
      onSuccess: () => this.#finishProbe(probe, { permissionGranted: true }),
      onError: (error) => {
        this.#finishProbe(probe);
        this.initialPromptPending = true;
        this.#showPrompt(guideUrl);
        console.error('[Autoplay Permission] Probe failed.', error);
      }
    });
  }

  #finishProbe(probe, { permissionGranted = false } = {}) {
    if (this.permissionProbe !== probe) return;

    this.blockedMedia.delete(probe);
    this.initialPromptPending = false;
    this.permissionGranted ||= permissionGranted;
    this.permissionProbe = null;
    probe.pause?.();
    probe.removeAttribute?.('src');
    probe.load?.();
  }

  #autoplayPolicy() {
    try {
      return globalThis.navigator?.getAutoplayPolicy?.('mediaelement');
    } catch {
      return undefined;
    }
  }

  #showPrompt(guideUrl) {
    const modalFrame = document.getElementById('modal');
    if (this.promptVisible && this.modalFrame?.isConnected && this.modalFrame === modalFrame) return;
    if (this.promptVisible) this.#clearPrompt();
    if (!guideUrl) return;
    if (!modalFrame) return;

    this.promptVisible = true;
    this.modalFrame = modalFrame;
    this.modalClosingHandler = () => this.#grantPermission();
    modalFrame.addEventListener('modal:closing', this.modalClosingHandler, { once: true });
    this.promptURL = new URL(guideUrl, document.baseURI || 'https://local.invalid').href;
    modalFrame.src = guideUrl;
    this.permissionTimer = setInterval(() => this.recheck(), 1000);
    this.beforeCacheHandler = () => this.#clearPrompt();
    document.addEventListener('turbo:before-cache', this.beforeCacheHandler, { once: true });
  }

  #promptCurrent() {
    return this.modalFrame?.isConnected &&
      new URL(this.modalFrame.src || '', document.baseURI || 'https://local.invalid').href === this.promptURL;
  }

  async recheck() {
    if (!this.promptVisible || this.rechecking) return;
    if (!this.#promptCurrent()) {
      this.#clearPrompt();
      return;
    }
    this.rechecking = true;
    try {
      // Retry the original probe without a user gesture, so only an actual
      // permission change dismisses the warning automatically.
      if (this.permissionProbe) {
        await this.permissionProbe.play();
        this.#finishProbe(this.permissionProbe, { permissionGranted: true });
      } else if (!this.permissionGranted && this.#autoplayPolicy() !== 'allowed') {
        return;
      }
      if (!this.#promptCurrent()) {
        this.#clearPrompt();
        return;
      }
      const dialog = this.modalFrame?.querySelector('dialog.utmr');
      dialog?.__ultimateTurboModalController?.hideModal();
    } catch {
      // The browser still blocks playback; keep the warning visible.
    } finally {
      this.rechecking = false;
    }
  }

  #grantPermission() {
    this.initialPromptPending = false;
    this.permissionGranted = true;
    const blockedMedia = [...this.blockedMedia];
    this.blockedMedia.clear();

    // Keep the user activation chain intact by playing before the modal changes.
    blockedMedia.forEach(([media, options]) => this.#attemptPlayback(media, options));
    this.#clearPrompt();
  }

  #clearPrompt() {
    clearInterval(this.permissionTimer);
    if (this.beforeCacheHandler) document.removeEventListener('turbo:before-cache', this.beforeCacheHandler);
    this.beforeCacheHandler = null;
    if (this.modalFrame && this.modalClosingHandler) {
      this.modalFrame.removeEventListener('modal:closing', this.modalClosingHandler);
    }

    this.modalFrame = null;
    this.modalClosingHandler = null;
    this.promptVisible = false;
  }
}

export const autoplayPermission = new AutoplayPermission();
